import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "@/lib/utils/utils"
import { HTMLAttributes } from "react"
import rehypeCallouts from "rehype-callouts"
import { useConfig } from "@/hooks/useConfig"
import { Text } from "@/components/Primitives/Text/text"
import { Link } from "react-router-dom"

interface MarkdownRendererProps {
  content: string
  className?: string
  variant?: "paragraph" | "small"
}

export function MarkdownRenderer({
  content,
  className,
  variant = "paragraph",
}: MarkdownRendererProps) {
  const { t } = useConfig()

  const components = {
    h1: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLHeadingElement>) => (
      <Text
        variant="h1"
        className={cn("mt-8 mb-4", elementClassName)}
        {...props}
      />
    ),
    h2: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLHeadingElement>) => (
      <Text
        variant="h2"
        className={cn("mt-8 mb-4", elementClassName)}
        {...props}
      />
    ),
    h3: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLHeadingElement>) => (
      <Text
        variant="h3"
        className={cn("mt-6 mb-3", elementClassName)}
        {...props}
      />
    ),
    h4: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLHeadingElement>) => (
      <Text
        variant="h4"
        className={cn("mt-6 mb-3", elementClassName)}
        {...props}
      />
    ),
    p: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLParagraphElement>) => (
      <Text
        variant={variant}
        className={cn("mt-4", className, elementClassName)}
        {...props}
      />
    ),
    div: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLDivElement>) => (
      <div {...props} className={cn("", elementClassName)} />
    ),
    a: ({
      className: elementClassName,
      href,
      ...props
    }: HTMLAttributes<HTMLAnchorElement> & { href?: string }) => {
      // Check if it's an internal link (starts with / or #) or external
      const isInternal = href && (href.startsWith("/") || href.startsWith("#"))

      if (isInternal) {
        return (
          <Link
            to={href}
            className={cn("text-purple-500 font-semibold", elementClassName)}
            {...(props as any)}
          >
            {props.children}
          </Link>
        )
      }

      // External links use regular anchor tags
      return (
        <a
          href={href}
          {...props}
          className={cn("text-purple-500", elementClassName)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {props.children}
        </a>
      )
    },
    blockquote: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLQuoteElement>) => (
      <Text
        variant="blockquote"
        as="blockquote"
        className={cn("my-6", elementClassName)}
        {...props}
      />
    ),
    ul: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLUListElement>) => (
      <ul
        {...props}
        className={cn("list-check pl-6 space-y-2 mt-6", elementClassName)}
      />
    ),
    ol: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLOListElement>) => (
      <ol
        {...props}
        className={cn("list-decimal pl-6 space-y-2 mt-6", elementClassName)}
      />
    ),
    li: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLLIElement>) => (
      <li {...props} className={cn("mt-2", elementClassName)} />
    ),
    strong: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLElement>) => (
      <strong className={cn("font-semibold", elementClassName)} {...props} />
    ),
    em: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLElement>) => (
      <em className={cn("italic", elementClassName)} {...props} />
    ),
    code: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLElement>) => (
      <code
        {...props}
        className={cn(
          "relative rounded bg-muted px-[0.3em] py-[0.2em] font-mono text-sm",
          elementClassName,
        )}
      />
    ),
    pre: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLPreElement>) => (
      <pre
        {...props}
        className={cn(
          "mb-4 mt-6 overflow-x-auto rounded-lg border bg-black py-4",
          elementClassName,
        )}
      />
    ),
    hr: ({
      className: elementClassName,
      ...props
    }: HTMLAttributes<HTMLHRElement>) => (
      <hr className={cn("my-4 border-t", elementClassName)} {...props} />
    ),
  }
  return (
    <div
      className={cn(
        "prose dark:prose-invert max-w-none [&>*]:text-inherit text-neutral-900",
        className,
      )}
    >
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          [
            rehypeCallouts,
            {
              callouts: {
                note: {
                  title: t("config_markdown_callout_note"),
                },
              },
              showIndicator: false,
            },
          ],
        ]}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
